/**
 * Phase 3.43 — Hauptbereich „Werkstatt / Teile / Zubehör".
 *
 * Extrahiert aus DealerProfileSection: Konfiguration von Werkstatt
 * (inkl. Online-Terminbuchung, Hol-/Bringservice, Service-Dauern) und
 * Teile-/Zubehör-Onlineshop. Speichert via PATCH /api/users/me/dealer-profile.
 */
import { useEffect, useState } from "react";
import { Wrench, Tag, Plus, Trash2, X } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { CheckRow, useEmployees, EmployeeSelect } from "./_shared";
import BranchOverrideToggle from "./BranchOverrideToggle";
import OrgLockedSection from "./OrgLockedSection";
import SaveStatusIndicator from "./SaveStatusIndicator";
import { useAutosave } from "../../hooks/useAutosave";
import { WorkshopScheduleEditor, PickupPartnerSuggestions } from "./_workshopHelpers";
import WorkshopLinkageBlock from "./WorkshopLinkageBlock";
import { InvoiceQuickPositionsPanel } from "./FinanzenSection";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import useSettingsExpandAll from "../../lib/useSettingsExpandAll";

const WORKSHOP_FIELDS = [
    // Phase 3.43 — Händlertyp (von DealerProfileSection hierher verschoben)
    // dealer_motorcycles/dealer_bicycles wurden in Phase 3.44 (Iter 2) nach Stammdaten zurückverschoben
    "dealer_free", "dealer_authorized", "dealer_authorized_brands",
    "dealer_workshop",
    "workshop_radius_enabled", "workshop_radius_km",
    // Jun-2026 (User-Wunsch) — Werkstatt-Verknüpfung
    "workshop_linkage_mode", "workshop_central_dealer_id", "workshop_central_dealer_name",
    "workshop_supply_days", "workshop_served_dealer_ids",
    "workshop_contact_first_name", "workshop_contact_last_name",
    "workshop_contact_phone", "workshop_contact_email", "workshop_contact_employee_id",
    "workshop_bikevinci_enabled", "workshop_bikevinci_server",
    "workshop_online_booking_enabled", "workshop_online_booking_use_own",
    "workshop_online_booking_url", "workshop_online_booking_use_platform",
    "workshop_booking_slots", "workshop_booking_advance_days",
    "workshop_booking_capacity", "workshop_service_durations",
    "workshop_reminder_email_enabled", "workshop_reminder_sms_enabled",
    "workshop_pickup_enabled", "workshop_pickup_self", "workshop_pickup_partner",
    "workshop_accept_user_discount",
    "dealer_parts",
    "parts_shop_url_enabled", "parts_shop_url",
    "parts_accept_user_discount",
    // Jun-2026 (User-Wunsch c) — Auto-Werkstattauftrag früh (bei Vertragsabschluss)
    "dispo_auto_workshop_order_early",
    // Jun-2026 (User-Wunsch) — zentral gepflegter Standard-Einleitungstext für Rechnungen
    "default_invoice_intro",
    "default_invoice_legal_footer",
];

function buildInitial(user) {
    return {
        // Händlertyp (Verkaufskategorien wurden in Stammdaten ausgelagert)
        dealer_free: !!user.dealer_free,
        dealer_authorized: !!user.dealer_authorized,
        dealer_authorized_brands: user.dealer_authorized_brands || [],
        dealer_workshop: !!user.dealer_workshop,
        workshop_radius_enabled: !!user.workshop_radius_enabled,
        workshop_radius_km: user.workshop_radius_km ?? "",
        // Jun-2026 (User-Wunsch) — Werkstatt-Verknüpfung
        workshop_linkage_mode: user.workshop_linkage_mode || "standalone",
        workshop_central_dealer_id: user.workshop_central_dealer_id || "",
        workshop_central_dealer_name: user.workshop_central_dealer_name || "",
        workshop_supply_days: user.workshop_supply_days || [],
        workshop_served_dealer_ids: user.workshop_served_dealer_ids || [],
        workshop_contact_first_name: user.workshop_contact_first_name || "",
        workshop_contact_last_name: user.workshop_contact_last_name || "",
        workshop_contact_phone: user.workshop_contact_phone || "",
        workshop_contact_email: user.workshop_contact_email || "",
        workshop_contact_employee_id: user.workshop_contact_employee_id || "",
        workshop_bikevinci_enabled: !!user.workshop_bikevinci_enabled,
        workshop_bikevinci_server: user.workshop_bikevinci_server || "",
        workshop_online_booking_enabled: !!user.workshop_online_booking_enabled,
        workshop_online_booking_use_own: !!user.workshop_online_booking_use_own,
        workshop_online_booking_url: user.workshop_online_booking_url || "",
        workshop_online_booking_use_platform: !!user.workshop_online_booking_use_platform,
        workshop_booking_slots: user.workshop_booking_slots || null,
        workshop_booking_advance_days: user.workshop_booking_advance_days || 14,
        workshop_booking_capacity: user.workshop_booking_capacity || 1,
        workshop_service_durations: user.workshop_service_durations || null,
        workshop_reminder_email_enabled: user.workshop_reminder_email_enabled !== false,
        workshop_reminder_sms_enabled: !!user.workshop_reminder_sms_enabled,
        workshop_pickup_enabled: !!user.workshop_pickup_enabled,
        workshop_pickup_self: !!user.workshop_pickup_self,
        workshop_pickup_partner: !!user.workshop_pickup_partner,
        workshop_accept_user_discount: !!user.workshop_accept_user_discount,
        dealer_parts: !!user.dealer_parts,
        parts_shop_url_enabled: !!user.parts_shop_url_enabled,
        parts_shop_url: user.parts_shop_url || "",
        parts_accept_user_discount: !!user.parts_accept_user_discount,
        dispo_auto_workshop_order_early: !!user.dispo_auto_workshop_order_early,
        default_invoice_intro: user.default_invoice_intro || "",
        default_invoice_legal_footer: user.default_invoice_legal_footer || "",
    };
}

function BackfillCategoryButton() {
    const [busy, setBusy] = useState(false);
    const run = async () => {
        if (!window.confirm("Bestehende Rechnungen & Aufträge der gesamten Organisation jetzt nachträglich mit dem Fahrzeugtyp taggen?")) return;
        setBusy(true);
        try {
            const r = await api.post("/dealer/backfill-vehicle-category");
            const d = r.data || {};
            toast.success(`Getaggt: ${d.invoices_tagged ?? 0} Rechnungen, ${d.work_orders_tagged ?? 0} Aufträge`);
        } catch (e) { toast.error(e.response?.data?.detail || "Backfill fehlgeschlagen"); }
        setBusy(false);
    };
    return (
        <div className="flex items-center justify-between gap-3 bg-white border border-indigo-200 rounded-sm p-3 mb-3" data-testid="backfill-category-block">
            <div className="text-xs text-gray-600">
                <span className="font-bold text-gray-800">Bestehende Belege taggen:</span> Ordnet alten Rechnungen/Aufträgen den Fahrzeugtyp zu — die „Häufig genutzt"-Vorschläge werden so sofort kategoriescharf.
            </div>
            <Button type="button" variant="outline" onClick={run} disabled={busy} className="rounded-sm h-9 shrink-0" data-testid="backfill-category-run">
                {busy ? "Läuft…" : "Jetzt taggen"}
            </Button>
        </div>
    );
}

function WorkOrderQuickPositionsPanel({ currentUser }) {
    const [rows, setRows] = useState(null);
    const [saving, setSaving] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(true);
    const [loadingSug, setLoadingSug] = useState(false);
    useEffect(() => {
        api.get("/dealer/work-order-quick-positions")
            .then((r) => setRows(r.data?.positions || []))
            .catch(() => setRows([]));
    }, []);
    // Jun-2026 (User-Wunsch) — Positionen aus den bestehenden Aufträgen vorschlagen.
    const loadSuggestions = () => {
        setLoadingSug(true);
        api.get("/dealer/work-order-positions/frequent", { params: { limit: 12 } })
            .then((r) => setSuggestions(r.data?.positions || []))
            .catch(() => setSuggestions([]))
            .finally(() => setLoadingSug(false));
    };
    useEffect(() => { loadSuggestions(); }, []);
    const addSuggestion = (s) => {
        setRows((arr) => {
            const exists = (arr || []).some((r) => (r.description || "").trim().toLowerCase() === (s.description || "").trim().toLowerCase());
            if (exists) { toast.info("Position ist bereits in der Tabelle."); return arr; }
            return [...(arr || []), {
                label: s.label || s.description?.slice(0, 24) || "", description: s.description || "",
                type: s.type || "labour", unit: s.unit || "AW",
                unit_price_net: s.unit_price_net || 0, vat_rate: s.vat_rate ?? 19, duration_min: null,
            }];
        });
    };
    const setRow = (i, k, v) => setRows((arr) => arr.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
    const addRow = () => setRows((arr) => [...(arr || []), { label: "", description: "", type: "labour", unit: "AW", unit_price_net: 0, vat_rate: 19, duration_min: null }]);
    const removeRow = (i) => setRows((arr) => arr.filter((_, idx) => idx !== i));
    const save = async () => {
        setSaving(true);
        try {
            const r = await api.put("/dealer/work-order-quick-positions", { positions: rows || [] });
            setRows(r.data?.positions || []);
            toast.success("Auftrags-Positionen gespeichert");
        } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
        setSaving(false);
    };
    const unusedSuggestions = (suggestions || []).filter((s) => !(rows || []).some((r) => (r.description || "").trim().toLowerCase() === (s.description || "").trim().toLowerCase()));
    if (rows === null) return <div className="text-sm text-gray-400">Lade…</div>;
    return (
        <div className="bg-gray-50 border border-gray-200 rounded-sm p-4" data-testid="wo-quick-positions-panel">
            <div className="flex items-center gap-2 mb-1">
                <Wrench className="w-4 h-4 text-gray-500" />
                <h4 className="font-bold text-sm uppercase tracking-wider text-gray-700">Auftrags-Positionen (Dauer &amp; Preis)</h4>
                <div className="ml-auto">
                    <BranchOverrideToggle area="auftrags_schnellpositionen" currentUser={currentUser}
                        infoText="Auftrags-Positionen (Termin-Dauer & Preis) des Hauptsitzes auf alle Filialen übernehmen & überschreiben."
                        testId="auftrags-schnellpos-branch-override" />
                </div>
            </div>
            <p className="text-xs text-gray-600 mb-3">Standard-Positionen mit Termin-Dauer (Min.), Preis &amp; MwSt. — erscheinen als Schnell-Chips in der Auftragsanlage. Die Dauer steuert zugleich die Slot-Länge im Werkstatt-Kalender. Jederzeit nachbearbeitbar.</p>
            <BackfillCategoryButton />
            {/* Aus meinen Aufträgen — häufig genutzte Positionen übernehmen (Jun-2026, User-Wunsch). */}
            <div className="bg-white border border-gray-200 rounded-sm p-3 mb-3" data-testid="wo-quick-suggestions">
                <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-bold text-gray-800">Aus meinen Aufträgen</span>
                    <span className="text-[11px] text-gray-500">— häufig genutzte Positionen aus vergangenen Werkstattaufträgen.</span>
                    <div className="ml-auto flex items-center gap-2">
                        <Button type="button" variant="outline" onClick={loadSuggestions} disabled={loadingSug} className="rounded-sm h-7 text-xs px-2" data-testid="wo-quick-suggestions-refresh">
                            {loadingSug ? "Lade…" : "Aktualisieren"}
                        </Button>
                        <button type="button" onClick={() => setShowSuggestions((v) => !v)} className="text-xs text-gray-500 hover:text-[#111827]" data-testid="wo-quick-suggestions-toggle">
                            {showSuggestions ? "Ausblenden" : "Einblenden"}
                        </button>
                    </div>
                </div>
                {showSuggestions && (
                    unusedSuggestions.length === 0 ? (
                        <p className="text-[11px] text-gray-400" data-testid="wo-quick-suggestions-empty">
                            {loadingSug ? "Lade Vorschläge…" : "Keine weiteren Vorschläge — alle häufigen Positionen sind bereits übernommen oder es liegen noch zu wenige Aufträge vor."}
                        </p>
                    ) : (
                        <div className="flex flex-wrap gap-1.5" data-testid="wo-quick-suggestions-chips">
                            {unusedSuggestions.map((s, i) => (
                                <button key={i} type="button" onClick={() => addSuggestion(s)}
                                    className="inline-flex items-center gap-1.5 bg-gray-50 hover:bg-[#FFCC00]/20 border border-gray-200 rounded-full px-2.5 py-1 text-xs transition-colors"
                                    title={`${s.description} · ${(s.unit_price_net || 0).toFixed?.(2) || s.unit_price_net} € · ${s.count}× genutzt`}
                                    data-testid={`wo-quick-suggestion-${i}`}>
                                    <Plus className="w-3 h-3 text-[#111827]" />
                                    <span className="font-medium">{s.label || s.description}</span>
                                    <span className="text-[10px] text-gray-400 font-mono-num">{s.count}×</span>
                                </button>
                            ))}
                        </div>
                    )
                )}
            </div>
            <div className="space-y-2">
                {rows.map((r, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`wo-quick-row-${i}`}>
                        <Input value={r.label || ""} onChange={(e) => setRow(i, "label", e.target.value)} placeholder="Label" className="col-span-12 md:col-span-2 rounded-sm h-9" data-testid={`wo-quick-label-${i}`} />
                        <Input value={r.description || ""} onChange={(e) => setRow(i, "description", e.target.value)} placeholder="Beschreibung" className="col-span-12 md:col-span-3 rounded-sm h-9" data-testid={`wo-quick-desc-${i}`} />
                        <select value={r.type || "labour"} onChange={(e) => setRow(i, "type", e.target.value)} className="col-span-4 md:col-span-1 h-9 border border-gray-300 rounded-sm px-1 text-sm bg-white" data-testid={`wo-quick-type-${i}`}>
                            <option value="labour">Lohn</option>
                            <option value="part">Teil</option>
                        </select>
                        <div className="col-span-4 md:col-span-2 relative">
                            <Input type="number" min="0" step="15" value={r.duration_min ?? ""} onChange={(e) => setRow(i, "duration_min", e.target.value === "" ? null : Number(e.target.value))} placeholder="Dauer" className="rounded-sm h-9 pr-9 font-mono-num" data-testid={`wo-quick-duration-${i}`} />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">min</span>
                        </div>
                        <Input type="number" step="0.01" value={r.unit_price_net ?? 0} onChange={(e) => setRow(i, "unit_price_net", e.target.value)} placeholder="0,00 €" className="col-span-4 md:col-span-2 rounded-sm h-9" data-testid={`wo-quick-price-${i}`} />
                        <select value={r.vat_rate ?? 19} onChange={(e) => setRow(i, "vat_rate", Number(e.target.value))} className="col-span-3 md:col-span-1 h-9 border border-gray-300 rounded-sm px-1 text-sm bg-white" data-testid={`wo-quick-vat-${i}`}>
                            <option value={19}>19%</option>
                            <option value={7}>7%</option>
                            <option value={0}>0%</option>
                        </select>
                        <button type="button" onClick={() => removeRow(i)} className="col-span-1 text-gray-400 hover:text-red-600 flex justify-center" title="Entfernen" data-testid={`wo-quick-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                ))}
            </div>
            <div className="flex items-center gap-2 mt-3">
                <Button type="button" variant="outline" onClick={addRow} className="rounded-sm h-9" data-testid="wo-quick-add"><Plus className="w-4 h-4 mr-1" /> Position</Button>
                <div className="flex-1" />
                <Button type="button" onClick={save} disabled={saving} className="rounded-sm h-9 bg-[#111827] hover:bg-black text-white font-bold" data-testid="wo-quick-save">{saving ? "Speichere…" : "Speichern"}</Button>
            </div>
        </div>
    );
}


export function WerkstattTeileZubehoerSection({ currentUser }) {
    const { refresh } = useAuth();
    const [expanded, setExpanded] = useState(false);
    useSettingsExpandAll(setExpanded);
    const [form, setForm] = useState(buildInitial(currentUser));
    const [brands, setBrands] = useState({ motorcycle: [], bicycle: [], scooter_ebike: [], all: [] });
    const [authBrandFilter, setAuthBrandFilter] = useState("");
    const employees = useEmployees();

    useEffect(() => {
        setForm(buildInitial(currentUser));
    }, [currentUser.id, currentUser.updated_at]);

    useEffect(() => {
        api.get("/brands").then((r) => setBrands(r.data)).catch(() => {});
    }, []);

    const update = (key, val) => {
        setForm((p) => ({ ...p, [key]: val }));
    };

    const toggleBrand = (list, brand, max = null) => {
        const has = list.includes(brand);
        if (has) return list.filter((b) => b !== brand);
        if (max && list.length >= max) {
            toast.error(`Maximal ${max} auswählbar`);
            return list;
        }
        return [...list, brand];
    };

    // Marken-Pool basierend auf den in Stammdaten persistierten Verkaufskategorien
    const allowedBrands = (() => {
        const m = currentUser.dealer_motorcycles !== false;
        const b = !!currentUser.dealer_bicycles;
        if (m && !b) return [...new Set([...(brands.motorcycle || []), ...(brands.scooter_ebike || [])])].sort();
        if (b && !m) return brands.bicycle || [];
        return brands.all || [];
    })();
    const filteredAuthBrands = allowedBrands.filter((b) => b.toLowerCase().includes(authBrandFilter.toLowerCase()));

    // Etappe 3 (Global Autosave) — Werkstatt-Einstellungen speichern automatisch.
    const persist = async (f) => {
        const payload = {};
        for (const k of WORKSHOP_FIELDS) {
            payload[k] = f[k] === "" ? null : f[k];
        }
        if (payload.workshop_radius_km !== null && payload.workshop_radius_km !== undefined && payload.workshop_radius_km !== "") {
            payload.workshop_radius_km = parseInt(payload.workshop_radius_km, 10) || null;
        }
        await api.patch("/users/me/dealer-profile", payload);
    };
    const { saving, dirty, savedAt } = useAutosave(form, persist, { enabled: expanded });

    return (
        <div className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid="werkstatt-teile-section">
            <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className={`w-full flex items-center gap-3 px-6 py-4 text-left hover:bg-gray-50 transition-colors ${expanded ? "border-b border-gray-200" : ""}`}
                data-testid="werkstatt-teile-toggle"
                aria-expanded={expanded}
            >
                <Wrench className="w-5 h-5 text-[#FFCC00] shrink-0" />
                <div className="flex-1 min-w-0">
                    <h2 className="font-display text-lg font-bold">Werkstatt / Teile / Zubehör</h2>
                    <p className="text-xs text-gray-600 mt-1">
                        Werkstatt-Konfiguration, Online-Terminbuchung, Hol-/Bringservice und Teile-Shop.
                    </p>
                </div>
                <span className={`text-gray-400 text-lg transition-transform shrink-0 ${expanded ? "rotate-180" : ""}`} aria-hidden="true">⌄</span>
            </button>
            {expanded && (
                <div className="p-6 space-y-4" data-testid="werkstatt-teile-body">
                    {/* Schnellpositionen (Jun-2026, User-Wunsch) — Auftrags- UND Rechnungs-
                        Schnellpositionen gebündelt unter Werkstatt / Teile / Zubehör. */}
                    <OrgLockedSection area="auftrags_schnellpositionen" label="Auftrags-Schnellpositionen"><WorkOrderQuickPositionsPanel currentUser={currentUser} /></OrgLockedSection>
                    <OrgLockedSection area="rechnungs_schnellpositionen" label="Rechnungs-Schnellpositionen">
                    <div className="bg-gray-50 border border-gray-200 rounded-sm p-4" data-testid="invoice-quickpos-wrapper">
                        <div className="flex items-center gap-2 mb-1">
                            <Tag className="w-4 h-4 text-gray-500" />
                            <h4 className="font-bold text-sm uppercase tracking-wider text-gray-700">Rechnungs-Schnellpositionen</h4>
                            <div className="ml-auto">
                                <BranchOverrideToggle area="rechnungs_schnellpositionen" currentUser={currentUser}
                                    infoText="Rechnungs-Schnellpositionen des Hauptsitzes auf alle Filialen übernehmen & überschreiben."
                                    testId="rechnungs-schnellpos-branch-override" />
                            </div>
                        </div>
                        <InvoiceQuickPositionsPanel />
                    </div>
                    </OrgLockedSection>
                    {/* === Händlertyp (Mehrfachauswahl) === */}
                    <OrgLockedSection area="haendlertyp" label="Der Händlertyp">
                    <div className="bg-gray-50 border border-gray-200 rounded-sm p-4" data-testid="dealer-type-panel">
                        <div className="flex items-center gap-2 mb-3">
                            <Wrench className="w-4 h-4 text-gray-500" />
                            <h4 className="font-bold text-sm uppercase tracking-wider text-gray-700">Händlertyp</h4>
                            <div className="ml-auto">
                                <BranchOverrideToggle area="haendlertyp" currentUser={currentUser}
                                    infoText="Händlertyp (frei/Vertrag/Werkstatt & Vertrags-Marken) des Hauptsitzes auf alle Filialen übernehmen & überschreiben."
                                    testId="haendlertyp-branch-override" />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3" data-testid="dealer-type-row">
                            <CheckRow label="Freier Händler" checked={form.dealer_free} onChange={(v) => update("dealer_free", v)} testId="dealer-free-checkbox" />
                            <CheckRow label="Vertragshändler" checked={form.dealer_authorized} onChange={(v) => update("dealer_authorized", v)} testId="dealer-authorized-checkbox" />
                        </div>
                        {form.dealer_authorized && (
                            <div className="ml-8 mt-2 p-3 bg-white border border-gray-200 rounded-sm" data-testid="dealer-authorized-brands-block">
                                <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-1"><Tag className="w-3 h-3" /> Vertrags-Marken</div>
                                {(form.dealer_authorized_brands || []).length > 0 && (
                                    <div className="flex flex-wrap gap-1 mb-2" data-testid="auth-brand-selected-badges">
                                        {(form.dealer_authorized_brands || []).map((b) => (
                                            <span key={b} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-[#111827] text-white text-[11px] font-bold" data-testid={`auth-brand-badge-${b}`}>
                                                {b}
                                                <button type="button" onClick={() => update("dealer_authorized_brands", toggleBrand(form.dealer_authorized_brands || [], b))} className="hover:text-[#FFCC00]" aria-label={`${b} entfernen`} data-testid={`auth-brand-badge-remove-${b}`}>
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                                <Input
                                    placeholder="Marke suchen…"
                                    value={authBrandFilter}
                                    onChange={(e) => setAuthBrandFilter(e.target.value)}
                                    className="rounded-sm mb-2 h-8 text-sm"
                                    data-testid="auth-brand-filter"
                                />
                                <div className="flex flex-wrap gap-1 max-h-40 overflow-y-auto">
                                    {filteredAuthBrands.map((b) => (
                                        <button
                                            key={b}
                                            type="button"
                                            onClick={() => update("dealer_authorized_brands", toggleBrand(form.dealer_authorized_brands || [], b))}
                                            className={`px-2 py-1 text-xs rounded-sm border ${(form.dealer_authorized_brands || []).includes(b) ? "bg-[#111827] border-[#111827] text-white font-bold" : "bg-white border-gray-300 text-gray-700 hover:border-[#111827]"}`}
                                            data-testid={`auth-brand-${b}`}
                                        >
                                            {b}
                                        </button>
                                    ))}
                                </div>
                                {(form.dealer_authorized_brands || []).length > 0 && (
                                    <div className="mt-2 text-[11px] text-gray-500">{form.dealer_authorized_brands.length} Marke{form.dealer_authorized_brands.length !== 1 ? "n" : ""} ausgewählt</div>
                                )}
                            </div>
                        )}
                    </div>
                    </OrgLockedSection>

                    {/* === Werkstatt === */}
                    <CheckRow label="Werkstatt" hint="Sie bieten Reparaturen, Wartung, Inspektion an" checked={form.dealer_workshop} onChange={(v) => update("dealer_workshop", v)} testId="dealer-workshop-checkbox" />
                    {form.dealer_workshop && (
                        <div className="ml-8 mt-2 mb-3 p-4 bg-gray-50 border border-gray-200 rounded-sm space-y-4" data-testid="workshop-details-panel">
                            <div className="text-xs font-bold uppercase tracking-wider text-gray-500">Werkstatt-Details</div>

                            {/* Service-Radius */}
                            <div>
                                <CheckRow label="Service-Radius" hint="Bestimmt das Einzugsgebiet aus dem Anfragen für Ihre Werkstatt an den Händler vermittelt werden." checked={form.workshop_radius_enabled} onChange={(v) => update("workshop_radius_enabled", v)} testId="workshop-radius-checkbox" />
                                {form.workshop_radius_enabled && (
                                    <div className="ml-7 mt-1 flex items-center gap-2" data-testid="workshop-radius-input-row">
                                        <Input
                                            type="number"
                                            min="0"
                                            max="1000"
                                            value={form.workshop_radius_km}
                                            onChange={(e) => update("workshop_radius_km", e.target.value)}
                                            placeholder="z. B. 50"
                                            className="rounded-sm h-9 w-32 font-mono-num"
                                            data-testid="workshop-radius-km-input"
                                        />
                                        <span className="text-xs text-gray-600">km Radius</span>
                                    </div>
                                )}
                            </div>

                            {/* Jun-2026 (User-Wunsch) — Werkstatt-Verknüpfung (Standort-Logik) */}
                            <WorkshopLinkageBlock value={form} onChange={update} />

                            {/* Öffnungszeiten wurden nach Stammdaten verschoben (Phase 3.44) */}

                            {/* Ansprechpartner */}
                            <div data-testid="workshop-contact-block">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2">Ansprechpartner Werkstatt</div>
                                <div className="mb-2">
                                    <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Mitarbeiter verknüpfen (optional)</Label>
                                    <div className="mt-1 max-w-md">
                                        <EmployeeSelect
                                            employees={employees}
                                            value={form.workshop_contact_employee_id}
                                            onSelect={(id, emp) => {
                                                update("workshop_contact_employee_id", id || "");
                                                if (emp) {
                                                    update("workshop_contact_first_name", emp.first_name || (emp.name || "").split(" ")[0] || form.workshop_contact_first_name);
                                                    update("workshop_contact_last_name", emp.last_name || (emp.name || "").split(" ").slice(1).join(" ") || form.workshop_contact_last_name);
                                                    if (emp.email) update("workshop_contact_email", emp.email);
                                                    if (emp.mobile_phone || emp.phone) update("workshop_contact_phone", emp.mobile_phone || emp.phone);
                                                }
                                            }}
                                            placeholder="— kein Mitarbeiter —"
                                            testId="workshop-contact-employee-select"
                                        />
                                    </div>
                                    <p className="text-[10px] text-gray-400 mt-1">Bei Auswahl werden Kontaktdaten vorausgefüllt — Felder bleiben manuell anpassbar.</p>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    <div>
                                        <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Sammelruf Werkstatt</Label>
                                        <Input value={form.workshop_contact_phone} onChange={(e) => update("workshop_contact_phone", e.target.value)} className="rounded-sm h-9 mt-1 font-mono-num" placeholder="+49 …" data-testid="workshop-contact-phone" />
                                    </div>
                                    <div>
                                        <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Sammel-E-Mail Werkstatt</Label>
                                        <Input type="email" value={form.workshop_contact_email} onChange={(e) => update("workshop_contact_email", e.target.value)} className="rounded-sm h-9 mt-1" placeholder="werkstatt@…" data-testid="workshop-contact-email" />
                                    </div>
                                </div>
                            </div>

                            {/* bikeVinci */}
                            <div>
                                <CheckRow label="Teilnahme an BikeVinci" hint="Die spezialisierte Zweirad-KI für Werkstatt, Service und Vertrieb." checked={form.workshop_bikevinci_enabled} onChange={(v) => update("workshop_bikevinci_enabled", v)} testId="workshop-bikevinci-checkbox" />
                                {form.workshop_bikevinci_enabled && (
                                    <div className="ml-7 mt-1" data-testid="workshop-bikevinci-server-row">
                                        <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">bikeVinci Serveradresse</Label>
                                        <Input
                                            value={form.workshop_bikevinci_server}
                                            onChange={(e) => update("workshop_bikevinci_server", e.target.value)}
                                            placeholder="https://api.bikevinci.example/v1"
                                            className="rounded-sm h-9 mt-1 font-mono-num text-sm"
                                            data-testid="workshop-bikevinci-server-input"
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Online Terminbuchung */}
                            <div>
                                <CheckRow
                                    label="Online Terminbuchung"
                                    hint="Kunden können online Werkstatt-Termine buchen"
                                    checked={form.workshop_online_booking_enabled}
                                    onChange={(v) => update("workshop_online_booking_enabled", v)}
                                    testId="workshop-online-booking-checkbox"
                                />
                                {form.workshop_online_booking_enabled && (
                                    <div className="ml-7 mt-2 space-y-2" data-testid="workshop-online-booking-options">
                                        <CheckRow
                                            label="Eigene Online-Buchungsseite verwenden"
                                            hint="Link zu Ihrer externen Buchungsseite"
                                            checked={form.workshop_online_booking_use_own}
                                            onChange={(v) => update("workshop_online_booking_use_own", v)}
                                            testId="workshop-online-booking-use-own-checkbox"
                                        />
                                        {form.workshop_online_booking_use_own && (
                                            <div className="ml-7" data-testid="workshop-online-booking-url-row">
                                                <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">URL zur Online-Buchungsseite</Label>
                                                <Input
                                                    type="url"
                                                    value={form.workshop_online_booking_url}
                                                    onChange={(e) => update("workshop_online_booking_url", e.target.value)}
                                                    placeholder="https://termine.ihre-werkstatt.de"
                                                    className="rounded-sm h-9 mt-1 font-mono-num text-sm"
                                                    data-testid="workshop-online-booking-url-input"
                                                />
                                            </div>
                                        )}
                                        <CheckRow
                                            label="Zweiradhändler-Terminbuchungsseite nutzen"
                                            hint="Termine werden an die hinterlegte Werkstatt-E-Mail gesendet"
                                            checked={form.workshop_online_booking_use_platform}
                                            onChange={(v) => update("workshop_online_booking_use_platform", v)}
                                            testId="workshop-online-booking-use-platform-checkbox"
                                        />
                                        {form.workshop_online_booking_use_platform && (
                                            <WorkshopScheduleEditor
                                                value={form.workshop_booking_slots}
                                                advanceDays={form.workshop_booking_advance_days}
                                                onChange={(v) => update("workshop_booking_slots", v)}
                                                onAdvanceChange={(n) => update("workshop_booking_advance_days", n)}
                                                capacity={form.workshop_booking_capacity}
                                                onCapacityChange={(n) => update("workshop_booking_capacity", n)}
                                                durations={form.workshop_service_durations}
                                                onDurationsChange={(d) => update("workshop_service_durations", d)}
                                            />
                                        )}
                                        {/* Phase 3.44 — Termin-Erinnerungen */}
                                        <div className="mt-3 pt-3 border-t border-gray-200" data-testid="workshop-reminder-block">
                                            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2">Termin-Erinnerungen an Kunden</div>
                                            <CheckRow
                                                label="Termin-Erinnerung per E-Mail"
                                                hint="Kunden erhalten 24 h vor dem Termin eine Erinnerungs-E-Mail (kostenlos)."
                                                checked={form.workshop_reminder_email_enabled}
                                                onChange={(v) => update("workshop_reminder_email_enabled", v)}
                                                testId="workshop-reminder-email-checkbox"
                                            />
                                            <CheckRow
                                                label="Termin-Erinnerung per SMS"
                                                hint="Kunden erhalten 24 h vor dem Termin eine Erinnerungs-SMS (kostenpflichtig, ca. 0,09 € pro SMS)."
                                                checked={form.workshop_reminder_sms_enabled}
                                                onChange={(v) => update("workshop_reminder_sms_enabled", v)}
                                                testId="workshop-reminder-sms-checkbox"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Hol- & Bringservice */}
                            <div>
                                <CheckRow
                                    label="Hol- & Bringservice"
                                    hint="Sie holen das Fahrzeug ab und bringen es nach dem Service zurück"
                                    checked={form.workshop_pickup_enabled}
                                    onChange={(v) => update("workshop_pickup_enabled", v)}
                                    testId="workshop-pickup-checkbox"
                                />
                                {form.workshop_pickup_enabled && (
                                    <div className="ml-7 mt-2 space-y-2" data-testid="workshop-pickup-options">
                                        <CheckRow
                                            label="Wir holen und bringen selbst innerhalb des Service-Radius"
                                            checked={form.workshop_pickup_self}
                                            onChange={(v) => update("workshop_pickup_self", v)}
                                            testId="workshop-pickup-self-checkbox"
                                        />
                                        <CheckRow
                                            label="Wir vermitteln das Holen und Bringen an einen Dienstleister"
                                            checked={form.workshop_pickup_partner}
                                            onChange={(v) => update("workshop_pickup_partner", v)}
                                            testId="workshop-pickup-partner-checkbox"
                                        />
                                        {form.workshop_pickup_partner && <PickupPartnerSuggestions />}
                                    </div>
                                )}
                            </div>

                            {/* Transaktions-Code Rabatt */}
                            <div className="border-t border-gray-200 pt-3 mt-1">
                                <CheckRow
                                    label="Transaktions-Code registrierter User anerkennen"
                                    hint="Sie gewähren registrierten Nutzern dieser Plattform entsprechende Rabatte auf Werkstatt-Dienstleistungen."
                                    checked={form.workshop_accept_user_discount}
                                    onChange={(v) => update("workshop_accept_user_discount", v)}
                                    testId="workshop-accept-user-discount-checkbox"
                                />
                                {form.workshop_accept_user_discount && (
                                    <p className="ml-7 mt-1 text-[11px] text-gray-600 leading-relaxed" data-testid="workshop-accept-user-discount-hint">
                                        Mit dieser Auswahl erklären Sie sich einverstanden, registrierten Benutzern dieser Seite die entsprechenden Rabatte auf Werkstatt-Dienstleistungen zu gewähren.
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Jun-2026 (User-Wunsch c) — Auto-Werkstattauftrag früh erzeugen */}
                    <OrgLockedSection area="montageauftrag_frueh" label="Die Einstellung „Montageauftrag früh erzeugen“">
                    <div className="bg-amber-50 border border-amber-200 rounded-sm p-4" data-testid="dispo-early-wo-panel">
                        <div className="flex items-start gap-2">
                            <div className="flex-1">
                                <CheckRow
                                    label="Montageauftrag früh erzeugen (bei Vertragsabschluss)"
                                    hint="Legt den Montage-Werkstattauftrag für Zubehör/Dienstleistungen bereits beim Versand des Kaufvertrags an — statt erst beim Auslieferungstermin. Gibt der Werkstatt mehr Vorlaufzeit. Im Kaufvertrag pro Vorgang übersteuerbar. Idempotent; Kategorie Auslieferungen (intern)."
                                    checked={form.dispo_auto_workshop_order_early}
                                    onChange={(v) => update("dispo_auto_workshop_order_early", v)}
                                    testId="dispo-early-wo-checkbox"
                                />
                            </div>
                            <div className="pt-2 shrink-0">
                                <BranchOverrideToggle area="montageauftrag_frueh" currentUser={currentUser}
                                    infoText="Einstellung „Montageauftrag früh erzeugen“ des Hauptsitzes auf alle Filialen übernehmen & überschreiben."
                                    testId="montageauftrag-frueh-branch-override" />
                            </div>
                        </div>
                    </div>
                    </OrgLockedSection>

                    {/* Jun-2026 (User-Wunsch) — zentral gepflegter Standard-Einleitungstext für Rechnungen */}
                    <OrgLockedSection area="rechnung_einleitung" label="Der Standard-Einleitungstext der Rechnung">
                    <div className="bg-gray-50 border border-gray-200 rounded-sm p-4" data-testid="default-invoice-intro-panel">
                        <div className="flex items-center gap-2 mb-1">
                            <Tag className="w-4 h-4 text-gray-500" />
                            <h4 className="font-bold text-sm uppercase tracking-wider text-gray-700">Standard-Einleitungstext Rechnung</h4>
                            <div className="ml-auto">
                                <BranchOverrideToggle area="rechnung_einleitung" currentUser={currentUser}
                                    infoText="Standard-Einleitungstext der Rechnung des Hauptsitzes auf alle Filialen übernehmen & überschreiben."
                                    testId="rechnung-einleitung-branch-override" />
                            </div>
                        </div>
                        <p className="text-xs text-gray-600 mb-2">
                            Wird automatisch über den Positionen jeder Rechnung eingefügt, sofern in der Rechnungsmaske kein eigener Text gesetzt ist (z. B. „Vielen Dank für Ihren Kauf!").
                        </p>
                        <Textarea
                            value={form.default_invoice_intro || ""}
                            onChange={(e) => update("default_invoice_intro", e.target.value)}
                            className="rounded-sm min-h-[70px] bg-white"
                            placeholder="z. B. Vielen Dank für Ihren Kauf!"
                            data-testid="default-invoice-intro-input"
                        />
                    </div>
                    </OrgLockedSection>

                    {/* Jun-2026 (User-Wunsch) — pro Händler konfigurierbarer Rechtstext in der Rechnungs-Fußzeile */}
                    <OrgLockedSection area="rechnung_fusszeile" label="Der Standard-Rechtstext (Fußzeile)">
                    <div className="bg-gray-50 border border-gray-200 rounded-sm p-4" data-testid="default-invoice-legal-panel">
                        <div className="flex items-center gap-2 mb-1">
                            <Tag className="w-4 h-4 text-gray-500" />
                            <h4 className="font-bold text-sm uppercase tracking-wider text-gray-700">Standard-Rechtstext Rechnung (Fußzeile)</h4>
                            <div className="ml-auto">
                                <BranchOverrideToggle area="rechnung_fusszeile" currentUser={currentUser}
                                    infoText="Standard-Rechtstext (Rechnungs-Fußzeile) des Hauptsitzes auf alle Filialen übernehmen & überschreiben."
                                    testId="rechnung-fusszeile-branch-override" />
                            </div>
                        </div>
                        <p className="text-xs text-gray-600 mb-2">
                            Erscheint als rechtlicher Hinweis am Ende jeder Rechnung (z. B. AGB-Hinweis, Eigentumsvorbehalt, Gerichtsstand). Leer lassen → neutraler Standardtext mit Ihrem Gerichtsstand wird automatisch verwendet. Eine Zeile pro Hinweis.
                        </p>
                        <Textarea
                            value={form.default_invoice_legal_footer || ""}
                            onChange={(e) => update("default_invoice_legal_footer", e.target.value)}
                            className="rounded-sm min-h-[90px] bg-white"
                            placeholder={"Es gelten unsere Allgemeinen Geschäftsbedingungen.\nGelieferte Ware bleibt bis zur vollständigen Bezahlung unser Eigentum.\nGerichtsstand ist …"}
                            data-testid="default-invoice-legal-input"
                        />
                    </div>
                    </OrgLockedSection>

                    {/* === Teile & Zubehör === */}
                    <OrgLockedSection area="teile_zubehoer" label="Teile & Zubehör">
                    <div className="flex items-start gap-2">
                        <div className="flex-1">
                            <CheckRow label="Teile & Zubehör" hint="Sie führen Ersatzteile und Zubehör" checked={form.dealer_parts} onChange={(v) => update("dealer_parts", v)} testId="dealer-parts-checkbox" />
                        </div>
                        <div className="pt-2 shrink-0">
                            <BranchOverrideToggle area="teile_zubehoer" currentUser={currentUser}
                                infoText="Teile & Zubehör (inkl. Onlineshop-Link) des Hauptsitzes auf alle Filialen übernehmen & überschreiben."
                                testId="teile-zubehoer-branch-override" />
                        </div>
                    </div>
                    {form.dealer_parts && (
                        <div className="ml-8 mt-2 mb-3 p-4 bg-gray-50 border border-gray-200 rounded-sm" data-testid="parts-details-panel">
                            <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Teile-Onlineshop</div>
                            <CheckRow label="Onlineshop-Link veröffentlichen" hint="Wird im Profil und unter Inseraten verlinkt" checked={form.parts_shop_url_enabled} onChange={(v) => update("parts_shop_url_enabled", v)} testId="parts-shop-url-checkbox" />
                            {form.parts_shop_url_enabled && (
                                <div className="ml-7 mt-1" data-testid="parts-shop-url-row">
                                    <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Onlineshop-Adresse</Label>
                                    <Input
                                        type="url"
                                        value={form.parts_shop_url}
                                        onChange={(e) => update("parts_shop_url", e.target.value)}
                                        placeholder="https://shop.ihre-domain.de"
                                        className="rounded-sm h-9 mt-1 font-mono-num text-sm"
                                        data-testid="parts-shop-url-input"
                                    />
                                </div>
                            )}
                            <div className="mt-3 pt-3 border-t border-gray-200">
                                <CheckRow
                                    label="Transaktions-Code registrierter User anerkennen"
                                    hint="Sie gewähren registrierten Nutzern dieser Plattform entsprechende Rabatte auf Waren und Zubehör."
                                    checked={form.parts_accept_user_discount}
                                    onChange={(v) => update("parts_accept_user_discount", v)}
                                    testId="parts-accept-user-discount-checkbox"
                                />
                                {form.parts_accept_user_discount && (
                                    <p className="ml-7 mt-1 text-[11px] text-gray-600 leading-relaxed" data-testid="parts-accept-user-discount-hint">
                                        Mit dieser Auswahl erklären Sie sich einverstanden, registrierten Benutzern dieser Seite die entsprechenden Rabatte auf Waren und Dienstleistungen zu gewähren.
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                    </OrgLockedSection>

                    {/* Autosave (Etappe 3) — speichert automatisch, kein Button mehr. */}
                    <div className="flex justify-end pt-3 border-t border-gray-200" data-testid="werkstatt-teile-autosave">
                        <SaveStatusIndicator saving={saving} dirty={dirty} savedAt={savedAt} />
                    </div>
                </div>
            )}
        </div>
    );
}

/**
 * Phase 3.44 — Mini-Vorschau-Karte für eingeklappten Zustand.
 * Zeigt Live-Status der wichtigsten Werkstatt/Teile-Einstellungen als kompakte Chip-Reihe.
 */
function WerkstattTeilePreview({ form }) {
    const chips = [];
    if (form.dealer_workshop) {
        chips.push({ label: "Werkstatt aktiv", tone: "on" });
        if (form.workshop_radius_enabled && form.workshop_radius_km) {
            chips.push({ label: `${form.workshop_radius_km} km Radius`, tone: "info" });
        }
        if (form.workshop_online_booking_enabled) {
            chips.push({ label: "Online-Buchung an", tone: "info" });
        }
        if (form.workshop_pickup_enabled) {
            chips.push({ label: "Hol- & Bring", tone: "info" });
        }
        if (form.workshop_reminder_sms_enabled) {
            chips.push({ label: "SMS-Erinnerung", tone: "warn" });
        }
    } else {
        chips.push({ label: "Werkstatt: aus", tone: "off" });
    }
    if (form.dealer_authorized && (form.dealer_authorized_brands || []).length > 0) {
        chips.push({ label: `${form.dealer_authorized_brands.length} Vertragsmarken`, tone: "info" });
    }
    if ((currentUser.favorite_brands || []).length > 0) {
        chips.push({ label: `Top-${currentUser.favorite_brands.length} Marken`, tone: "info" });
    }
    if (form.dealer_parts) {
        chips.push({
            label: form.parts_shop_url_enabled && form.parts_shop_url ? "Teile-Shop verlinkt" : "Teile & Zubehör",
            tone: "on",
        });
    }
    if (chips.length === 0) return null;
    const toneClasses = {
        on: "bg-emerald-100 text-emerald-800 border-emerald-200",
        off: "bg-gray-100 text-gray-600 border-gray-200",
        info: "bg-white text-gray-700 border-gray-300",
        warn: "bg-amber-100 text-amber-800 border-amber-200",
    };
    return (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="werkstatt-teile-preview">
            {chips.map((c, i) => (
                <span
                    key={i}
                    className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-bold uppercase tracking-wider ${toneClasses[c.tone] || toneClasses.info}`}
                    data-testid={`werkstatt-preview-chip-${i}`}
                >
                    {c.label}
                </span>
            ))}
        </div>
    );
}

